// Międzyczasy na żywo /live/{alias} (makiety „Wyniki – makiety”: L1–L3). Czysta logika (bez Vue),
// liczona raz na pobranie /splits — szablon tylko czyta gotowe teksty.
//
// Kolumny: punkty pośrednie w kolejności `sort` (bez startów 1/7 — dawniej punkt startu był drugą
// kolumną „Start”), meta (8/93) osobno jako kolumna „Meta”. Komórka: czas od startu, godzina i miejsce
// na punkcie. Miejsce liczone po czasie od startu (start interwałowy), tylko zawodnicy klasyfikowani
// (także DNF — przeszli punkt), remisy dzielą miejsce. Przejście przez punkt = najwcześniejsze podbicie
// z surowej listy `splits` (runner.splits w magazynie trzyma ostatnie z odpowiedzi API).
import { isClassified } from './runner-role.js'
import { formatClock, formatDuration, parseApiTime } from './polish-time.js'

export const START_TYPES = [1, 7]
export const FINISH_TYPES = [8, 93]
// do tylu punktów (z metą) kropki postępu na telefonie, powyżej pasek z „x/y”
export const DOTS_MAX = 12

function markerType(marker) {
  return Number(marker && marker.type)
}

// "PK1|Przełęcz" → "PK1 Przełęcz"; bez nazwy — „PK {sort}”
export function pointLabel(marker) {
  const text = String(marker?.text || '').split('|').map(p => p.trim()).filter(Boolean).join(' ')
  if (text) return text
  if (FINISH_TYPES.includes(markerType(marker))) return 'Meta'
  return `PK ${marker?.sort ?? marker?.id ?? ''}`.trim()
}

// markers.distance jest w METRACH (5200 → 5,2 km). Brak, 0 albo niepoprawna wartość → null.
export function metersToKm(distance) {
  if (distance == null || distance === '') return null
  const n = Number(distance)
  if (!Number.isFinite(n) || n <= 0) return null
  return n / 1000
}

// markers.distance (m): 5200 → "5,2 km"; brak albo 0 → ''
export function kmText(distance) {
  const km = metersToKm(distance)
  if (km == null) return ''
  return `${km.toLocaleString('pl-PL', { maximumFractionDigits: 1 })} km`
}

/**
 * Kolumny tabeli z markerów /splits.
 * @returns {{points: Array<{key, markerId, index, label, km}>, finish: {key, km}}}
 */
export function buildLiveColumns(markers) {
  const ordered = (markers || []).filter(Boolean).slice().sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0))
  const finishMarker = ordered.find(m => FINISH_TYPES.includes(markerType(m))) || null
  const points = ordered
    .filter(m => !START_TYPES.includes(markerType(m)) && !FINISH_TYPES.includes(markerType(m)))
    .map((m, index) => ({ key: String(m.id), markerId: m.id, index, label: pointLabel(m), km: kmText(m.distance) }))
  return {
    points,
    finish: { key: finishMarker ? String(finishMarker.id) : null, km: kmText(finishMarker?.distance) }
  }
}

// runner_id → (marker_id → najwcześniejsza chwila przejścia w ms)
export function firstPasses(splits) {
  const byRunner = new Map()
  for (const split of splits || []) {
    if (!split) continue
    const ms = parseApiTime(split.time)
    if (ms == null) continue
    const runnerKey = String(split.runner_id)
    let passes = byRunner.get(runnerKey)
    if (!passes) {
      passes = new Map()
      byRunner.set(runnerKey, passes)
    }
    const markerKey = String(split.marker_id)
    const prev = passes.get(markerKey)
    if (prev == null || ms < prev) passes.set(markerKey, ms)
  }
  return byRunner
}

// Remisy dzielą miejsce: czasy [10, 12, 12, 15] → miejsca [1, 2, 2, 4].
function assignPlaces(entries) {
  entries.sort((a, b) => a.fromStartMs - b.fromStartMs)
  let place = 0
  let prev = null
  entries.forEach((entry, index) => {
    if (entry.fromStartMs !== prev) {
      place = index + 1
      prev = entry.fromStartMs
    }
    entry.cell.place = place
  })
}

function compareProgress(a, b) {
  if (a.lastIndex !== b.lastIndex) return b.lastIndex - a.lastIndex
  const at = a.lastIndex >= 0 ? a.cells[a.lastIndex].fromStartMs : null
  const bt = b.lastIndex >= 0 ? b.cells[b.lastIndex].fromStartMs : null
  if (at != null && bt != null && at !== bt) return at - bt
  if (at != null && bt == null) return -1
  if (at == null && bt != null) return 1
  return (Number(a.number) || 0) - (Number(b.number) || 0)
}

/**
 * Wiersze /live: wiersze z buildResultsRows (utils/results-list.js) + komórki punktów.
 * DNS są pomijani (jak w dawnym /live). Na mecie zostaje kolejność wyników (czas z karą);
 * w trasie i DNF — najdalszy punkt, potem czas od startu na tym punkcie, potem numer.
 *
 * @param {Array} baseRows  wynik buildResultsRows (kolejność wyników)
 * @param {Object} opts
 *   results: wyniki z magazynu (rola zawodnika)
 *   columns: buildLiveColumns(...)
 *   splits: surowa lista podbić z /splits
 */
export function buildLiveRows(baseRows, { results = [], columns, splits = [] } = {}) {
  const passes = firstPasses(splits)
  const runnerById = new Map(results.map(r => [String(r.id), r]))
  const points = columns.points
  const placeEntries = points.map(() => [])

  const rows = []
  for (const base of baseRows) {
    if (base.group === 'dns') continue
    const runner = runnerById.get(String(base.id))
    const own = passes.get(String(base.id))
    const startMs = base.startMs
    const cells = points.map(col => {
      const ms = own ? (own.get(col.key) ?? null) : null
      const fromStartMs = ms != null && startMs != null && ms >= startMs ? ms - startMs : null
      return {
        reached: ms != null,
        ms,
        fromStartMs,
        fromText: fromStartMs != null ? formatDuration(fromStartMs) : '',
        clockText: ms != null ? formatClock(ms) : '',
        place: null,
        isLast: false
      }
    })
    let lastIndex = -1
    cells.forEach((cell, i) => { if (cell.reached) lastIndex = i })
    if (runner && isClassified(runner)) {
      cells.forEach((cell, i) => {
        if (cell.fromStartMs != null) placeEntries[i].push({ fromStartMs: cell.fromStartMs, cell })
      })
    }
    const finished = base.group === 'finished'
    const finishFromMs = finished && base.finishMs != null && startMs != null && base.finishMs >= startMs ? base.finishMs - startMs : null
    rows.push({
      ...base,
      penaltyComment: runner?.penaltyComment || '',
      cells,
      lastIndex,
      meta: finished
        ? {
            fromText: finishFromMs != null ? formatDuration(finishFromMs) : '',
            clockText: base.finishClock,
            gpsOnly: base.gpsOnly
          }
        : null
    })
  }
  placeEntries.forEach(assignPlaces)

  // teksty zależne od miejsc (liczone po assignPlaces)
  for (const row of rows) {
    const last = row.lastIndex >= 0 ? row.cells[row.lastIndex] : null
    if (row.group === 'course' && last) last.isLast = true
    row.lastLabel = last ? points[row.lastIndex].label : ''
    row.lastFromText = last ? last.fromText : ''
    row.lastPlace = last ? last.place : null
    // „w drodze do …”: nazwa następnego punktu albo „mety”
    row.nextLabel = row.lastIndex + 1 < points.length ? points[row.lastIndex + 1].label : 'mety'
    row.reachedCount = row.cells.filter(c => c.reached).length + (row.group === 'finished' ? 1 : 0)
    row.progress = [...row.cells.map(c => c.reached), row.group === 'finished']
  }

  const finished = rows.filter(r => r.group === 'finished')
  const course = rows.filter(r => r.group === 'course').sort(compareProgress)
  const dnf = rows.filter(r => r.group === 'dnf').sort(compareProgress)
  return [...finished, ...course, ...dnf]
}

// "PK4 Polana · 2:12:40 · 14." — ostatni punkt zawodnika w trasie (telefon, L2)
export function lastPointText(row) {
  if (!row.lastLabel) return ''
  return [row.lastLabel, row.lastFromText, row.lastPlace ? `${row.lastPlace}.` : ''].filter(Boolean).join(' · ')
}
